import { Currency, CurrencyAmount, Percent } from '@kodiak-finance/sdk-core'
import { Pool, Position, TickMath } from '@kodiak-finance/v3-sdk'
import { useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useV3PositionFees } from 'hooks/useV3PositionFees'
import { ReactNode, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { PositionDetails } from 'types/position'
import { unwrappedToken } from 'utils/unwrappedToken'

import { AppState } from '../../index'
import { selectPercent } from './actions'

export function useBurnV3State(): AppState['burnV3'] {
  return useAppSelector((state) => state.burnV3)
}

export function useDerivedV3BurnInfo(
  position?: PositionDetails,
  asWETH = false
): {
  position?: Position
  liquidityPercentage?: Percent
  liquidityValue0?: CurrencyAmount<Currency>
  liquidityValue1?: CurrencyAmount<Currency>
  feeValue0?: CurrencyAmount<Currency>
  feeValue1?: CurrencyAmount<Currency>
  outOfRange: boolean
  error?: ReactNode
} {
  const { account } = useConnection()
  const { percent } = useBurnV3State()

  const [token0, token1] = useTokens([position?.token0, position?.token1])

  const pool = useMemo(() => {
    if (!token0 || !token1 || !position?.fee || !position?.pool) {
      return undefined
    }

    try {
      const [sortedToken0, sortedToken1] = token0.sortsBefore(token1) ? [token0, token1] : [token1, token0]

      return new Pool(
        sortedToken0,
        sortedToken1,
        position.fee,
        position.pool.sqrtPrice || TickMath.getSqrtRatioAtTick(position.poolTick || 0),
        position.pool.liquidity || '0',
        position.poolTick || 0
      )
    } catch (error) {
      console.error('Error creating pool in useDerivedV3BurnInfo:', error)
      return undefined
    }
  }, [token0, token1, position?.fee, position?.pool, position?.poolTick])

  const positionSDK = useMemo(() => {
    if (
      !pool ||
      !position?.liquidity ||
      typeof position?.tickLower !== 'number' ||
      typeof position?.tickUpper !== 'number'
    ) {
      return undefined
    }

    try {
      return new Position({
        pool,
        liquidity: position.liquidity.toString(),
        tickLower: position.tickLower,
        tickUpper: position.tickUpper,
      })
    } catch (error) {
      console.error('Error creating position in useDerivedV3BurnInfo:', error)
      return undefined
    }
  }, [pool, position])

  const liquidityPercentage = useMemo(() => new Percent(percent, 100), [percent])

  const { discountedAmount0, discountedAmount1 } = useMemo(() => {
    if (!positionSDK || !token0 || !token1) {
      return { discountedAmount0: undefined, discountedAmount1: undefined }
    }

    const isInputToken0Sorted = token0.sortsBefore(token1)
    const amount0 = isInputToken0Sorted ? positionSDK.amount0 : positionSDK.amount1
    const amount1 = isInputToken0Sorted ? positionSDK.amount1 : positionSDK.amount0

    const discounted0 = liquidityPercentage.multiply(amount0.quotient).quotient
    const discounted1 = liquidityPercentage.multiply(amount1.quotient).quotient

    return {
      discountedAmount0: discounted0,
      discountedAmount1: discounted1,
    }
  }, [liquidityPercentage, positionSDK, token0, token1])

  const liquidityValue0 =
    token0 && discountedAmount0
      ? CurrencyAmount.fromRawAmount(asWETH ? token0 : unwrappedToken(token0), discountedAmount0)
      : undefined
  const liquidityValue1 =
    token1 && discountedAmount1
      ? CurrencyAmount.fromRawAmount(asWETH ? token1 : unwrappedToken(token1), discountedAmount1)
      : undefined

  const [feeValue0, feeValue1] = useV3PositionFees(
    position && token0 && token1
      ? { position, token0, token1, asWETH }
      : {
          position: undefined,
          token0: undefined,
          token1: undefined,
          asWETH,
        }
  )

  const outOfRange = useMemo(() => {
    if (!position) return false
    return !position.inRange
  }, [position])

  let error: ReactNode | undefined
  if (!account) {
    error = <>Connect Wallet</>
  }
  if (percent === 0) {
    error = error ?? <>Continue</>
  }
  return {
    position: positionSDK,
    liquidityPercentage,
    liquidityValue0,
    liquidityValue1,
    feeValue0,
    feeValue1,
    outOfRange,
    error,
  }
}

export function useBurnV3ActionHandlers(): {
  onPercentSelect: (percent: number) => void
} {
  const dispatch = useAppDispatch()

  const onPercentSelect = useCallback(
    (percent: number) => {
      dispatch(selectPercent({ percent }))
    },
    [dispatch]
  )

  return {
    onPercentSelect,
  }
}
